// MediaPipe Hands — 21 landmark connections (skeleton)
// Index reference: https://developers.google.com/mediapipe/solutions/vision/hand_landmarker
export const HAND_CONNECTIONS = [
  // thumb
  [0, 1], [1, 2], [2, 3], [3, 4],
  // index
  [0, 5], [5, 6], [6, 7], [7, 8],
  // middle
  [5, 9], [9, 10], [10, 11], [11, 12],
  // ring
  [9, 13], [13, 14], [14, 15], [15, 16],
  // pinky
  [13, 17], [17, 18], [18, 19], [19, 20],
  // palm
  [0, 17],
];

/**
 * Heuristic: detect "open hand" from 21 normalized landmarks.
 * Tip y < MCP y (smaller y = higher in image) for index/middle/ring/pinky.
 * Thumb tip 4 horizontally separated from index MCP 5.
 *
 * Returns: 'open' | 'closed' | 'none'
 */
export function detectHandShape(landmarks) {
  if (!landmarks || landmarks.length < 21) return 'none';

  const TIP = { index: 8, middle: 12, ring: 16, pinky: 20, thumb: 4 };
  const MCP = { index: 5, middle: 9, ring: 13, pinky: 17 };

  const fingerExtended = (tipIdx, mcpIdx) => {
    const tip = landmarks[tipIdx];
    const mcp = landmarks[mcpIdx];
    // smaller y = higher on screen. Add small margin so a flat hand still counts.
    return tip.y < mcp.y - 0.02;
  };

  const indexExt = fingerExtended(TIP.index, MCP.index);
  const middleExt = fingerExtended(TIP.middle, MCP.middle);
  const ringExt = fingerExtended(TIP.ring, MCP.ring);
  const pinkyExt = fingerExtended(TIP.pinky, MCP.pinky);

  const thumbTip = landmarks[TIP.thumb];
  const indexMcp = landmarks[MCP.index];
  const thumbExt = Math.abs(thumbTip.x - indexMcp.x) > 0.08;

  const extendedCount = [indexExt, middleExt, ringExt, pinkyExt, thumbExt].filter(Boolean).length;

  if (extendedCount >= 4) return 'open';
  return 'closed';
}

/**
 * Draws the 21 hand landmarks + skeleton onto a canvas context.
 * Coordinates are normalized (0..1).
 */
export function drawHand(ctx, landmarks, w, h, opts = {}) {
  const lineColor = opts.lineColor || '#A855F7';
  const dotColor = opts.dotColor || '#FFFFFF';
  const dotEdge = opts.dotEdge || '#7F00FF';

  // Glow pass for connections
  ctx.save();
  ctx.shadowColor = lineColor;
  ctx.shadowBlur = 14;
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 3.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.beginPath();
  for (const [a, b] of HAND_CONNECTIONS) {
    const pa = landmarks[a];
    const pb = landmarks[b];
    if (!pa || !pb) continue;
    ctx.moveTo(pa.x * w, pa.y * h);
    ctx.lineTo(pb.x * w, pb.y * h);
  }
  ctx.stroke();
  ctx.restore();

  // Landmarks (dots)
  ctx.save();
  ctx.shadowColor = dotEdge;
  ctx.shadowBlur = 10;
  for (let i = 0; i < landmarks.length; i++) {
    const p = landmarks[i];
    const cx = p.x * w;
    const cy = p.y * h;
    // Tips are slightly bigger
    const isTip = i === 4 || i === 8 || i === 12 || i === 16 || i === 20;
    const r = isTip ? 6.5 : 5;
    ctx.beginPath();
    ctx.fillStyle = dotColor;
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.strokeStyle = dotEdge;
    ctx.lineWidth = 2;
    ctx.arc(cx, cy, r + 0.5, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}
